/**
 * Types for the native Member List Deal (MLD) screens. Mirrors, field for
 * field, the digital-services contract of the four read-only `abo/mld` routes
 * (DS `src/mld/dto/mld-responses.ts` and `src/mld/domain/types.ts`; mld-native
 * TechSpec "API Endpoints", ADR-007).
 *
 * DS returns codes, never wording: enum codes, integer cents plus currency and
 * ISO-8601 date strings. ABO turns the codes into words in its strings files,
 * and an unknown code renders as "Unknown", never as a guessed state. The
 * client is read-only: there is no MLD write route.
 */

export type MldStatus =
  | 'TRIAL'
  | 'ACTIVE'
  | 'PAYMENT_DUE'
  | 'ON_HOLD'
  | 'CANCELLED';

export type MldFamily = 'AFY' | 'RM' | 'OTHER';

export type MldSubscriptionType =
  | 'MONTHLY_NO_CONTRACT'
  | 'MONTHLY_CONTRACTED'
  | 'ANNUAL_CONTRACTED'
  | 'ONE_TIME';

export type MldSaleType =
  | 'NON_CONTRACTED_SALE'
  | 'CONTRACTED_SALE'
  | 'BILLED_THROUGH'
  | 'RENEWAL_SALE'
  | 'FAILED_CREDIT_CARD_COLLECTION'
  | 'EXPANSION_SALE_SERVICES'
  | 'UPGRADE_DOWNGRADE'
  | 'REACTIVATION';

/**
 * Who caused an event. `AUTHORIFY_SYSTEM` is an Authorify service acting
 * through the Chargebee API (DS normalizer: source `api`); `SYSTEM` is an
 * import or an event of unknown origin.
 */
export type MldActor =
  | 'AUTOMATIC'
  | 'CUSTOMER'
  | 'SUPPORT'
  | 'AUTHORIFY_SYSTEM'
  | 'SYSTEM';

export type MldLedgerKind =
  | 'PAYMENT'
  | 'PAYMENT_FAILED'
  | 'STATUS_CHANGE'
  | 'PLAN_CHANGE'
  | 'CANCELLATION_SCHEDULED'
  | 'CANCELLATION_REMOVED'
  | 'IMPORTED';

export type MldWarningCode =
  | 'UNKNOWN_CHARGEBEE_STATUS'
  | 'CUSTOMER_NOT_RESOLVED'
  | 'NOT_TIED_TO_SUBSCRIPTION'
  | 'EVENT_FAILED'
  | 'LEDGER_CAPPED';

export interface MldDealMember {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

/** Every field is `null` while the deal's plan line was never seen. */
export interface MldDealPlan {
  itemPriceId: string | null;
  name: string | null;
  amountCents: number | null;
  currency: string | null;
  subscriptionType: MldSubscriptionType | null;
  billingPeriodUnit: string | null;
}

export interface MldLastPayment {
  invoiceId: string;
  amountCents: number;
  currency: string;
  /** ISO-8601. */
  paidAt: string;
  /** `null` = no sale type (trial payment, customer one-time charge). */
  saleType: MldSaleType | null;
  trialPayment: boolean;
  actor: MldActor;
  /** Set only for a payment taken in the Chargebee admin console. */
  operatorEmail: string | null;
  /**
   * The paid invoice's item, e.g. "52 printed book credits (upsell offer)".
   * Missing from a DS deploy older than this field: read it through
   * `mldLastPaymentItemDescription`, which returns `null` then.
   */
  itemDescription: string | null;
}

export interface MldOpenFailure {
  invoiceId: string;
  amountDueCents: number;
  /** ISO-8601. */
  failedAt: string;
  failureReason: string | null;
}

/** One row of the list (TechSpec route 1). */
export interface MldDealSummary {
  subscriptionId: string;
  /** The DS customer `_id`, `null` while the deal is not linked to one. */
  customerId: string | null;
  member: MldDealMember;
  family: MldFamily;
  plan: MldDealPlan;
  /** `null` when the Chargebee status is unknown; ABO shows "Unknown". */
  status: MldStatus | null;
  trialSale: boolean;
  /** ISO-8601. */
  cancelsOn: string | null;
  /** ISO-8601; `null` when CANCELLED or when a cancellation is scheduled. */
  nextPaymentAt: string | null;
  /**
   * ISO-8601: the date the current status began (trial start, active since,
   * first failure of the open invoice, on hold since or cancelled at); `null`
   * for an unknown status or a missing source.
   */
  statusSince: string | null;
  lastPayment: MldLastPayment | null;
  openFailure: MldOpenFailure | null;
  /** ISO-8601. */
  lastEventAt: string | null;
}

/** A ledger entry as DS stores it, with `at` as an ISO-8601 string. */
export interface MldLedgerEntryDto {
  eventId: string;
  at: string;
  kind: MldLedgerKind;
  actor: MldActor;
  operatorEmail: string | null;
  fromStatus?: MldStatus | null;
  toStatus?: MldStatus | null;
  /** PLAN_CHANGE, as itemPriceId. */
  fromPlan?: string;
  toPlan?: string;
  invoiceId?: string;
  /** Amount paid (PAYMENT) or amount due (PAYMENT_FAILED), integer cents. */
  amountCents?: number;
  currency?: string;
  /** `null` = no sale type (trial payment, customer one-time charge, import). */
  saleType?: MldSaleType | null;
  trialPayment?: boolean;
  itemDescription?: string;
  /** FAILED_CREDIT_CARD_COLLECTION: the failed invoice this payment settles. */
  settlesInvoiceId?: string;
  failureReason?: string | null;
  /** The raw Chargebee event `source` (e.g. `api`, `admin_console`). */
  source?: string;
  /** PAYMENT / PAYMENT_FAILED: the plan (else first charge) line's itemPriceId. */
  itemPriceId?: string;
  /** PAYMENT / PAYMENT_FAILED with a plan line: the plan's display name. */
  planName?: string;
  /** PAYMENT: the gross invoice total (tax included, before credits), cents. */
  grossAmountCents?: number;
  /** CANCELLATION_SCHEDULED: the cancellation target date, ISO-8601. */
  cancelsOn?: string;
}

/** ISO-8601 dates of the deal's lifecycle. */
export interface MldDealDates {
  trialStart: string | null;
  trialEnd: string | null;
  activeSince: string | null;
  onHoldSince: string | null;
  resumesOn: string | null;
  cancelledAt: string | null;
  /**
   * An open paid trial's conversion date (its next payment); `null` otherwise.
   * Missing from a DS deploy older than this field: read it through
   * `mldConvertsOn`, which returns `null` then.
   */
  convertsOn: string | null;
}

export interface MldWarning {
  code: MldWarningCode;
  detail: string;
  /** ISO-8601. */
  at: string;
}

/** One deal with its history (TechSpec routes 3 and 4). */
export interface MldDealView extends MldDealSummary {
  dates: MldDealDates;
  chargebeeCustomerId: string | null;
  /** Oldest first, as DS sends it (screens list it newest first). */
  ledger: MldLedgerEntryDto[];
  warnings: MldWarning[];
  imported: boolean;
}

/** Paginator meta; `currentPage` echoes the 0-based page DS was asked for. */
export interface MldListMeta {
  total: number;
  perPage: number;
  currentPage: number;
  lastPage: number;
}

/** Response of `GET /v1/abo/mld`. */
export interface MldListResponse {
  data: MldDealSummary[];
  meta: MldListMeta;
}

/** Response of `GET /v1/abo/mld/counts`; the six statuses sum to `all`. */
export interface MldCountsResponse {
  all: number;
  trial: number;
  active: number;
  paymentDue: number;
  onHold: number;
  cancelled: number;
  unknown: number;
}

export type MldOtherDeal = Pick<
  MldDealSummary,
  'subscriptionId' | 'family' | 'plan' | 'status'
>;

/** Response of `GET /v1/abo/mld/subscriptions/:subscriptionId`. */
export interface MldDetailResponse {
  deal: MldDealView;
  /** The same member's other deals, never the deal itself. */
  otherDeals: MldOtherDeal[];
  /** `https://<site>.chargebee.com`; ABO builds the Chargebee links from it. */
  chargebeeAppUrl: string;
}

/** Response of `GET /v1/abo/mld/member/:customerId`. */
export interface MldMemberResponse {
  customerId: string;
  /** Empty when the member has no deal. */
  deals: MldDealView[];
  chargebeeAppUrl: string;
}

/** The `status` filter of the list; absent means "All". */
export const MLD_STATUS_FILTERS = [
  'trial',
  'active',
  'payment_due',
  'on_hold',
  'cancelled',
] as const;

export type MldStatusFilter = (typeof MLD_STATUS_FILTERS)[number];

/** The list and counts only filter these two families. */
export const MLD_FAMILY_FILTERS = ['AFY', 'RM'] as const;

export type MldFamilyFilter = (typeof MLD_FAMILY_FILTERS)[number];

/**
 * The `sortBy` values of `GET /v1/abo/mld` (DS `MLD_SORT_BY_VALUES`, ADR-003):
 * the activity order (DS default) and the three date columns.
 */
export const MLD_SORT_BY_VALUES = [
  'activity',
  'lastPayment',
  'upcoming',
  'since',
] as const;

export type MldSortBy = (typeof MLD_SORT_BY_VALUES)[number];

export type MldSortDir = 'asc' | 'desc';

/** Rows per list page (the DS default). */
export const MLD_DEFAULT_PER_PAGE = 25;

/** DS validates `q` as 2 to 100 characters after trimming. */
export const MLD_SEARCH_MIN_LENGTH = 2;
export const MLD_SEARCH_MAX_LENGTH = 100;

/** The list and counts filters shared by both routes. */
export interface MldCountsParams {
  family?: MldFamilyFilter;
  /** Sent only when `true`. */
  cancelsSoon?: boolean;
  /** Sent only when `true`. */
  trialSale?: boolean;
  /** Free text; sent trimmed and only when at least 2 characters long. */
  q?: string;
}

export interface MldListParams extends MldCountsParams {
  status?: MldStatusFilter;
  /** 1-based UI page; sent to DS as the 0-based `page`. */
  page?: number;
  perPage?: number;
  /** Sent only when set; DS defaults to the activity order. */
  sortBy?: MldSortBy;
  /** Sent only when set; DS defaults to the key's first direction. */
  sortDir?: MldSortDir;
}

/** The query actually sent to `GET /v1/abo/mld/counts`. */
export interface MldCountsQuery {
  family?: MldFamilyFilter;
  cancelsSoon?: true;
  trialSale?: true;
  q?: string;
}

/** The query actually sent to `GET /v1/abo/mld`. */
export interface MldListQuery extends MldCountsQuery {
  status?: MldStatusFilter;
  /** 0-based. */
  page: number;
  perPage: number;
  sortBy?: MldSortBy;
  sortDir?: MldSortDir;
}

/** The 404 codes of the `abo/mld` routes (DS `abo-mld-refusal.ts`). */
export const MLD_NOT_ENABLED = 'MLD_NOT_ENABLED';
export const MLD_NOT_FOUND = 'MLD_NOT_FOUND';
export const MLD_CUSTOMER_NOT_FOUND = 'CUSTOMER_NOT_FOUND';

export type MldRefusalCode =
  | typeof MLD_NOT_ENABLED
  | typeof MLD_NOT_FOUND
  | typeof MLD_CUSTOMER_NOT_FOUND;
